import React, { useEffect, useMemo, useState } from 'react'
import {
  FaAward,
  FaCalendarAlt,
  FaCertificate,
  FaChartLine,
  FaClock,
  FaCogs,
  FaMedal,
  FaTools,
} from 'react-icons/fa'
import { BASE_URL_Upload } from '../Component/commonComponents/ApiEndpoints'
import { useSidebar } from '../Component/SidebarContext'

const REGISTRY_TITLE = 'Vocational/Skill Certificate'

const formatDdMmYyyy = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value.trim())) {
    return value.trim()
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const safeStr = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return fallback
  return s
}

const parsePercentage = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(String(v).replace(/%/g, '').trim())
  return Number.isFinite(n) ? n : null
}

const resolveMediaUrl = (src) => {
  const t = safeStr(src, '')
  if (!t) return ''
  if (/^https?:\/\//i.test(t)) return t
  if (t.startsWith('//')) return `https:${t}`
  const base = String(BASE_URL_Upload || '').replace(/\/$/, '')
  const path = t.startsWith('/') ? t : `/${t}`
  return base ? `${base}${path}` : t
}

const displayTitleFromProps = (certificateTitle) => {
  const t = safeStr(certificateTitle, '')
  if (!t || t === REGISTRY_TITLE) return 'Certificate of Vocational Training'
  return t
}

const parseSkillLevel = (certificate) => {
  const raw =
    certificate?.skill_level ??
    certificate?.skillLevel ??
    certificate?.level ??
    certificate?.training_level
  if (raw === null || raw === undefined || raw === '') return null
  const s = String(raw).toLowerCase()
  if (s.includes('begin')) return 'beginner'
  if (s.includes('inter')) return 'intermediate'
  if (s.includes('adv')) return 'advanced'
  return null
}

const parseSkillRemark = (certificate) => {
  const raw =
    certificate?.skill_remark ??
    certificate?.competency_remark ??
    certificate?.remark ??
    certificate?.performance_remark
  if (raw === null || raw === undefined || raw === '') return null
  const s = String(raw).toLowerCase()
  if (s.includes('highly') || s.includes('high')) return 'highly'
  if (s.includes('industry')) return 'industry'
  if (s.includes('competent') || s.includes('compet')) return 'competent'
  return safeStr(raw, '')
}

const CertificateTemplate4 = ({
  studentName = 'Student Name',
  courseName = 'Course Name',
  date = '',
  instituteName = 'Institute Name',
  certificateTitle = 'Certificate of Vocational Training',
  authorityName = 'Authorized Signatory',
  certificateId = '',
  logoUrl = '',
  student: studentProp,
  course: courseProp,
  certificate: certificateProp,
  institute: instituteProp,
  selectedStudent,
  selectedCourse,
  suppressSidebarOffset = false,
}) => {
  const certificate = certificateProp || {}
  const student = studentProp || selectedStudent || {}
  const course = courseProp || selectedCourse || {}
  const institute = instituteProp || {}

  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [photoFailed, setPhotoFailed] = useState(false)
  const [fallbackPhotoFailed, setFallbackPhotoFailed] = useState(false)

  useEffect(() => {
    if (suppressSidebarOffset) return undefined

    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed, suppressSidebarOffset])

  useEffect(() => {
    setPhotoFailed(false)
    setFallbackPhotoFailed(false)
  }, [student?.student_image, student?.photo, student?.image])

  useEffect(() => {
    const root = document.documentElement
    const onBeforePrint = () => root.classList.add('certificate-template-4-print-session')
    const onAfterPrint = () => root.classList.remove('certificate-template-4-print-session')
    window.addEventListener('beforeprint', onBeforePrint)
    window.addEventListener('afterprint', onAfterPrint)
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint)
      window.removeEventListener('afterprint', onAfterPrint)
      root.classList.remove('certificate-template-4-print-session')
    }
  }, [])

  const data = useMemo(() => {
    const instName =
      safeStr(institute.institute_name) ||
      safeStr(institute.name) ||
      safeStr(instituteName)

    const studName = safeStr(student.name) || safeStr(studentName)
    const father = safeStr(student.father_name)
    const mother = safeStr(student.mother_name)
    const admission = safeStr(student.admission_number)

    const crsName =
      safeStr(course.course_name) || safeStr(course.name) || safeStr(courseName)
    const durationRaw = course.course_duration ?? course.duration
    const duration = (() => {
      if (durationRaw === null || durationRaw === undefined) return ''
      const s = String(durationRaw).trim()
      if (!s) return ''
      return /month/i.test(s) ? s : `${s} months`
    })()

    const start = formatDdMmYyyy(course.start_date ?? course.startDate)
    const end = formatDdMmYyyy(course.end_date ?? course.endDate)

    const certNum =
      safeStr(certificate.certificate_number) ||
      safeStr(certificate.certificate_id) ||
      safeStr(certificate.id != null ? String(certificate.id) : '') ||
      safeStr(certificateId)

    const issueRaw =
      certificate.issue_date ?? certificate.date ?? certificate.issueDate ?? date
    const issue = formatDdMmYyyy(issueRaw) || (typeof issueRaw === 'string' ? issueRaw : '')

    const pct = parsePercentage(certificate.percentage ?? certificate.percent)
    const grade = safeStr(certificate.grade)

    const imgRaw =
      student.student_image ?? student.photo ?? student.image ?? student.profile_image
    const photoSrc = resolveMediaUrl(imgRaw)

    const titlePrinted = displayTitleFromProps(certificateTitle)
    const skillLevel = parseSkillLevel(certificate)
    const skillRemarkRaw = parseSkillRemark(certificate)

    return {
      instName,
      studName,
      father,
      mother,
      admission,
      crsName,
      duration,
      start,
      end,
      certNum,
      issue,
      pct,
      grade,
      photoSrc,
      titlePrinted,
      skillLevel,
      skillRemarkRaw,
    }
  }, [
    certificate,
    student,
    course,
    institute,
    studentName,
    courseName,
    instituteName,
    certificateId,
    certificateTitle,
    date,
  ])

  const {
    instName,
    studName,
    father,
    mother,
    admission,
    crsName,
    duration,
    start,
    end,
    certNum,
    issue,
    pct,
    grade,
    photoSrc,
    titlePrinted,
    skillLevel,
    skillRemarkRaw,
  } = data

  const parentsLine =
    father && mother
      ? `S/o ${father} and M/o ${mother}`
      : father
        ? `S/o ${father}`
        : mother
          ? `M/o ${mother}`
          : ''

  const trainingPeriod =
    start && end ? `${start} to ${end}` : start || end ? `${start || '—'} to ${end || '—'}` : ''

  const qrData = useMemo(() => {
    const parts = [instName, certNum || 'PREVIEW', issue || formatDdMmYyyy(new Date())].filter(
      Boolean,
    )
    return parts.join('|')
  }, [instName, certNum, issue])

  const qrSrc =
    typeof window !== 'undefined'
      ? `https://api.qrserver.com/v1/create-qr-code/?size=72x72&margin=0&data=${encodeURIComponent(
          qrData,
        )}`
      : ''

  const defaultPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
  const showPhoto = photoSrc && !photoFailed
  const logoResolved = resolveMediaUrl(logoUrl) || logoUrl

  const showCertifiedRibbon =
    certificate?.certified_professional === true ||
    certificate?.certified_professional === 1 ||
    certificate?.certified_professional === '1' ||
    (pct !== null && pct >= 65)

  const remarkCheck = (key) => {
    if (!skillRemarkRaw) return key === 'competent'
    if (skillRemarkRaw === 'highly') return key === 'highly'
    if (skillRemarkRaw === 'industry') return key === 'industry'
    if (skillRemarkRaw === 'competent') return key === 'competent'
    return false
  }

  const layoutMarginLeft = suppressSidebarOffset ? '0px' : marginLeft

  const handlePrint = () => {
    window.print()
  }

  return (
    <div
      className="certificate-template-4-page"
      style={{
        marginLeft: layoutMarginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Outfit:wght@600;700;800&display=swap');

          .certificate-template-4-page {
            min-height: 100vh;
            background: linear-gradient(160deg, #e0f2fe 0%, #ecfdf5 45%, #dbeafe 100%);
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding: 24px 16px 36px;
            box-sizing: border-box;
          }

          .certificate-template-4-wrap {
            width: min(297mm, 100%);
          }

          .certificate-template-4-actions {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
          }

          .certificate-template-4-print-btn {
            border: none;
            background: linear-gradient(135deg, #0d9488, #0369a1);
            color: #fff;
            padding: 10px 18px;
            border-radius: 10px;
            font-weight: 700;
            font-family: 'DM Sans', system-ui, sans-serif;
            font-size: 13px;
            cursor: pointer;
            box-shadow: 0 8px 20px rgba(13, 148, 136, 0.25);
          }

          .certificate-template-4-print-stage {
            display: block;
            width: 100%;
          }

          .certificate-template-4-sheet {
            --teal: #0d9488;
            --blue: #0369a1;
            --ink: #0f172a;
            --muted: #475569;
            box-sizing: border-box;
            width: 100%;
            aspect-ratio: 297 / 210;
            max-height: min(90vh, 210mm);
            background: #fff;
            position: relative;
            overflow: hidden;
            border-radius: 8px;
            box-shadow:
              0 20px 50px rgba(15, 23, 42, 0.12),
              inset 0 0 0 1px rgba(13, 148, 136, 0.35);
            border: 1px solid rgba(13, 148, 136, 0.35);
          }

          .certificate-template-4-watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Outfit', sans-serif;
            font-size: clamp(48px, 12vw, 100px);
            font-weight: 800;
            color: rgba(13, 148, 136, 0.06);
            letter-spacing: 0.15em;
            text-transform: uppercase;
            pointer-events: none;
            user-select: none;
            z-index: 0;
          }

          .certificate-template-4-topbar {
            height: 8px;
            background: linear-gradient(90deg, #0d9488 0%, #059669 40%, #0284c7 100%);
            position: relative;
            z-index: 2;
            flex-shrink: 0;
          }

          .certificate-template-4-inner {
            position: relative;
            z-index: 2;
            padding: clamp(14px, 2vw, 22px) clamp(16px, 2.5vw, 28px) 16px;
            height: calc(100% - 8px);
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }

          .certificate-template-4-head {
            text-align: center;
            flex-shrink: 0;
            margin-bottom: 10px;
          }

          .certificate-template-4-logo {
            width: 52px;
            height: 52px;
            border-radius: 10px;
            border: 2px solid rgba(13, 148, 136, 0.45);
            object-fit: cover;
            background: #fff;
            margin: 0 auto 6px;
            display: block;
          }

          .certificate-template-4-logo-ph {
            width: 52px;
            height: 52px;
            border-radius: 10px;
            border: 2px dashed rgba(100, 116, 139, 0.5);
            margin: 0 auto 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #94a3b8;
            font-family: 'DM Sans', sans-serif;
          }

          .certificate-template-4-institute {
            margin: 0;
            font-family: 'Outfit', sans-serif;
            font-size: clamp(13px, 1.6vw, 16px);
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--blue);
          }

          .certificate-template-4-title {
            margin: 6px 0 0;
            font-family: 'Outfit', sans-serif;
            font-size: clamp(20px, 2.6vw, 26px);
            font-weight: 800;
            letter-spacing: 0.04em;
            color: var(--ink);
            line-height: 1.2;
          }

          .certificate-template-4-ribbon {
            display: inline-block;
            margin-top: 8px;
            padding: 4px 14px;
            border-radius: 999px;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            background: linear-gradient(90deg, #0d9488, #0284c7);
            color: #fff;
            font-family: 'DM Sans', sans-serif;
          }

          .certificate-template-4-main {
            display: grid;
            grid-template-columns: 1fr min(120px, 22%);
            gap: 14px 18px;
            align-items: start;
            flex: 1;
            min-height: 0;
            flex-shrink: 0;
          }

          .certificate-template-4-prose {
            font-family: 'DM Sans', system-ui, sans-serif;
            font-size: clamp(11px, 1.15vw, 13px);
            line-height: 1.65;
            color: var(--muted);
            text-align: left;
            margin: 0;
          }

          .certificate-template-4-name {
            font-weight: 700;
            color: var(--ink);
            font-size: clamp(12px, 1.2vw, 14px);
          }

          .certificate-template-4-em {
            font-weight: 600;
            color: #1e293b;
          }

          .certificate-template-4-divider {
            height: 1px;
            background: linear-gradient(90deg, transparent, #cbd5e1, transparent);
            margin: 10px 0;
            flex-shrink: 0;
          }

          .certificate-template-4-stats {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
            margin-top: 8px;
          }

          .certificate-template-4-stat {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            padding: 8px 10px;
            background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            min-width: 0;
          }

          .certificate-template-4-stat svg {
            color: var(--teal);
            flex-shrink: 0;
            margin-top: 2px;
            font-size: 14px;
          }

          .certificate-template-4-stat-label {
            font-size: 8px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #64748b;
            font-weight: 700;
            font-family: 'DM Sans', sans-serif;
          }

          .certificate-template-4-stat-value {
            font-size: 11px;
            font-weight: 600;
            color: var(--ink);
            font-family: 'DM Sans', sans-serif;
            word-break: break-word;
          }

          .certificate-template-4-perf {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
            margin-top: 8px;
          }

          .certificate-template-4-remarks {
            display: flex;
            flex-wrap: wrap;
            gap: 8px 12px;
            margin-top: 8px;
            font-size: 10px;
            font-family: 'DM Sans', sans-serif;
            color: var(--ink);
          }

          .certificate-template-4-remark-tag {
            padding: 3px 10px;
            border-radius: 6px;
            border: 1px solid #cbd5e1;
            background: #fff;
          }

          .certificate-template-4-remark-tag--on {
            border-color: var(--teal);
            background: rgba(13, 148, 136, 0.08);
            font-weight: 600;
          }

          .certificate-template-4-side {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
          }

          .certificate-template-4-photo {
            width: 100%;
            max-width: 108px;
            aspect-ratio: 3 / 4;
            border: 2px solid #0d9488;
            border-radius: 6px;
            overflow: hidden;
            background: #f1f5f9;
            box-shadow: 0 4px 12px rgba(13, 148, 136, 0.15);
          }

          .certificate-template-4-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .certificate-template-4-photo-ph {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #94a3b8;
            padding: 4px;
            text-align: center;
          }

          .certificate-template-4-qr img {
            display: block;
            width: 56px;
            height: 56px;
          }

          .certificate-template-4-qr-cap {
            font-size: 7px;
            color: #64748b;
            text-align: center;
            margin-top: 2px;
          }

          .certificate-template-4-meta {
            margin-top: 8px;
            display: flex;
            flex-wrap: wrap;
            gap: 12px 20px;
            font-size: 10px;
            color: var(--muted);
            font-family: 'DM Sans', sans-serif;
          }

          .certificate-template-4-meta strong {
            color: var(--ink);
            font-weight: 600;
          }

          .certificate-template-4-footer {
            margin-top: auto;
            padding-top: 10px;
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 10px;
            flex-shrink: 0;
          }

          .certificate-template-4-sign {
            text-align: center;
            font-family: 'DM Sans', sans-serif;
          }

          .certificate-template-4-seal {
            width: 44px;
            height: 44px;
            margin: 0 auto 4px;
            border: 2px solid #64748b;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            color: #64748b;
            background: #f8fafc;
          }

          .certificate-template-4-seal svg {
            font-size: 18px;
          }

          .certificate-template-4-sign-line {
            border-top: 1px solid var(--ink);
            margin-top: 22px;
            padding-top: 5px;
            font-size: 9px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: var(--ink);
          }

          .certificate-template-4-sign-sub {
            font-size: 8px;
            color: var(--muted);
            margin-top: 2px;
            font-weight: 500;
            text-transform: none;
          }

          .certificate-template-4-badges {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin-top: 6px;
            justify-content: flex-start;
          }

          .certificate-template-4-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 9px;
            padding: 2px 8px;
            border-radius: 4px;
            background: #f1f5f9;
            color: #475569;
            border: 1px solid #e2e8f0;
            font-family: 'DM Sans', sans-serif;
          }

          @media (max-width: 720px) {
            .certificate-template-4-main {
              grid-template-columns: 1fr;
            }

            .certificate-template-4-side {
              flex-direction: row;
              justify-content: center;
              flex-wrap: wrap;
            }

            .certificate-template-4-stats,
            .certificate-template-4-perf {
              grid-template-columns: 1fr;
            }

            .certificate-template-4-footer {
              grid-template-columns: 1fr;
            }
          }

          @media print {
            /*
             * Printing from embedded preview (e.g. TemplateBasedCertificate):
             * - Do NOT use position:fixed here — it gets clipped by .template-preview-shell { overflow:hidden }.
             * - Do NOT use body * { visibility:hidden } — hidden nodes still consume layout height → extra blank pages.
             * - Hide the app sidebar only when this certificate is on the page (:has).
             */
            @page {
              size: A4 landscape;
              margin: 0;
            }

            html,
            body,
            #root {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: none !important;
              background: #fff !important;
              height: auto !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }

            /*
             * Sidenavbar renders a Fragment: [mobile Navbar fixed-top] + [desktop sidebar].
             * Hiding only :first-child leaves the desktop bar visible in print — hide every
             * App direct child except the main content column (always last).
             */
            .App:has(.certificate-template-4-page) > *:not(:last-child),
            html.certificate-template-4-print-session .App > *:not(:last-child) {
              display: none !important;
            }

            .App:has(.certificate-template-4-page) > *:last-child,
            html.certificate-template-4-print-session .App > *:last-child {
              width: 100% !important;
              max-width: 100% !important;
              /* flex:none collapses percentage-height descendants in print — keep growth */
              flex: 1 1 auto !important;
              min-width: 0 !important;
              /* App.js sets padding: 12px 0 — creates a large-looking top gap in print */
              padding: 0 !important;
              margin: 0 !important;
            }

            .template-based-certificate-root {
              margin-left: 0 !important;
              margin-right: 0 !important;
              margin-top: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              width: 100% !important;
              max-width: none !important;
              min-width: 0 !important;
              box-sizing: border-box !important;
            }

            .template-tc-print-preview-wrap {
              padding: 0 !important;
              margin: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              width: 100% !important;
              max-width: none !important;
              box-sizing: border-box !important;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .template-preview-shell,
            .template-preview-surface,
            .template-preview-surface > div {
              overflow: visible !important;
              max-height: none !important;
              max-width: none !important;
              width: 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              height: auto !important;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .certificate-template-4-page {
              position: relative !important;
              left: auto !important;
              top: auto !important;
              display: flex;
              flex-direction: column;
              align-items: stretch;
              justify-content: flex-start;
              box-sizing: border-box;
              width: 100% !important;
              max-width: none !important;
              min-height: 0 !important;
              max-height: none !important;
              height: auto !important;
              padding: 0 !important;
              margin: 0 !important;
              margin-left: 0 !important;
              margin-top: 0 !important;
              background: #fff !important;
              z-index: auto;
              overflow: visible !important;
              break-inside: avoid;
              page-break-inside: avoid;
              break-after: avoid;
              page-break-after: avoid;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-4-page,
            .certificate-template-4-page * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-4-wrap {
              width: 100%;
              max-width: none;
              margin: 0;
              flex-shrink: 0;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .certificate-template-4-actions,
            .certificate-template-4-actions * {
              display: none !important;
            }

            /*
             * Print layout: avoid position:absolute + height:100% + inner height:calc(100%-8px)
             * — Chrome print often resolves % heights to 0 so only watermark/topbar paint.
             */
            /* Fixed A4 landscape box so the certificate fills the page (not a small strip + blank area) */
            .certificate-template-4-print-stage {
              position: relative !important;
              display: flex !important;
              flex-direction: column !important;
              width: 100% !important;
              max-width: 297mm !important;
              min-width: 0 !important;
              height: 210mm !important;
              min-height: 210mm !important;
              max-height: 210mm !important;
              margin: 0 auto !important;
              margin-top: 0 !important;
              aspect-ratio: unset !important;
              flex-shrink: 0;
              break-inside: avoid !important;
              page-break-inside: avoid !important;
              overflow: hidden !important;
              box-sizing: border-box !important;
              transform: none !important;
              page-break-after: avoid !important;
            }

            .certificate-template-4-sheet {
              position: relative !important;
              inset: auto !important;
              left: auto !important;
              top: auto !important;
              right: auto !important;
              bottom: auto !important;
              flex: 1 1 auto !important;
              width: 100% !important;
              height: 100% !important;
              min-height: 0 !important;
              max-width: none !important;
              max-height: none !important;
              aspect-ratio: unset !important;
              display: flex !important;
              flex-direction: column !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              break-inside: avoid !important;
              page-break-inside: avoid !important;
              overflow: hidden !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-4-inner {
              position: relative !important;
              z-index: 2 !important;
              flex: 1 1 auto !important;
              height: auto !important;
              min-height: 0 !important;
              display: flex !important;
              flex-direction: column !important;
              box-sizing: border-box !important;
              break-inside: auto !important;
              page-break-inside: auto !important;
              padding: 10px 14px 12px !important;
            }

            .certificate-template-4-main {
              flex: 1 1 auto !important;
              min-height: 0 !important;
              gap: 10px 14px !important;
              align-content: start !important;
            }

            .certificate-template-4-footer {
              break-inside: auto !important;
              page-break-inside: auto !important;
              margin-top: auto !important;
              padding-top: 8px !important;
              gap: 8px !important;
              flex-shrink: 0 !important;
            }

            .certificate-template-4-head {
              margin-bottom: 4px !important;
            }

            .certificate-template-4-divider {
              margin: 5px 0 !important;
            }

            .certificate-template-4-stats,
            .certificate-template-4-perf {
              gap: 5px !important;
              margin-top: 4px !important;
            }

            .certificate-template-4-stat {
              padding: 5px 6px !important;
            }

            .certificate-template-4-remarks {
              margin-top: 4px !important;
              gap: 5px 8px !important;
            }

            .certificate-template-4-badges {
              margin-top: 3px !important;
              gap: 4px !important;
            }

            .certificate-template-4-meta {
              margin-top: 4px !important;
              gap: 8px 12px !important;
            }

            .certificate-template-4-sign-line {
              margin-top: 10px !important;
            }

            .certificate-template-4-watermark {
              font-size: 52px !important;
            }

            .certificate-template-4-main .certificate-template-4-prose {
              margin-top: 0 !important;
              line-height: 1.45 !important;
            }

            .certificate-template-4-main p.certificate-template-4-prose
              + p.certificate-template-4-prose {
              margin-top: 5px !important;
            }

            .certificate-template-4-print-stage,
            .certificate-template-4-sheet {
              page-break-before: avoid !important;
              break-before: avoid-page !important;
              page-break-after: avoid !important;
              break-after: avoid-page !important;
            }
          }
        `}
      </style>

      <div className="certificate-template-4-wrap">
        <div className="certificate-template-4-actions">
          <button type="button" className="certificate-template-4-print-btn" onClick={handlePrint}>
            Print / Download
          </button>
        </div>

        <div className="certificate-template-4-print-stage">
        <article className="certificate-template-4-sheet">
          <div className="certificate-template-4-watermark" aria-hidden>
            Skill Certified
          </div>
          <div className="certificate-template-4-topbar" />

          <div className="certificate-template-4-inner">
            <header className="certificate-template-4-head">
              {logoResolved ? (
                <img src={logoResolved} alt="" className="certificate-template-4-logo" />
              ) : (
                <div className="certificate-template-4-logo-ph">Logo</div>
              )}
              <p className="certificate-template-4-institute">{instName || 'Institute Name'}</p>
              <h1 className="certificate-template-4-title">{titlePrinted}</h1>
              {showCertifiedRibbon ? (
                <span className="certificate-template-4-ribbon">Certified Professional</span>
              ) : null}
            </header>

            <div className="certificate-template-4-main">
              <div>
                <p className="certificate-template-4-prose">
                  This is to certify that{' '}
                  <span className="certificate-template-4-name">{studName || '—'}</span>
                  {parentsLine ? (
                    <>
                      ,<br />
                      {parentsLine},
                    </>
                  ) : admission ? (
                    ','
                  ) : null}
                  {admission ? (
                    <>
                      <br />
                      bearing Admission No.{' '}
                      <span className="certificate-template-4-em">{admission}</span>,
                    </>
                  ) : null}
                  <br />
                  has successfully completed vocational training in{' '}
                  <span className="certificate-template-4-em">{crsName || '—'}</span>.
                </p>

                <p className="certificate-template-4-prose" style={{ marginTop: 10 }}>
                  The candidate has demonstrated proficiency in practical skills and has met the
                  required industry standards during the training period from{' '}
                  <span className="certificate-template-4-em">{start || '—'}</span> to{' '}
                  <span className="certificate-template-4-em">{end || '—'}</span>.
                </p>

                <div className="certificate-template-4-divider" />

                <div className="certificate-template-4-stats">
                  <div className="certificate-template-4-stat">
                    <FaTools aria-hidden />
                    <div>
                      <div className="certificate-template-4-stat-label">Course</div>
                      <div className="certificate-template-4-stat-value">{crsName || '—'}</div>
                    </div>
                  </div>
                  <div className="certificate-template-4-stat">
                    <FaClock aria-hidden />
                    <div>
                      <div className="certificate-template-4-stat-label">Duration</div>
                      <div className="certificate-template-4-stat-value">{duration || '—'}</div>
                    </div>
                  </div>
                  <div className="certificate-template-4-stat">
                    <FaCalendarAlt aria-hidden />
                    <div>
                      <div className="certificate-template-4-stat-label">Training period</div>
                      <div className="certificate-template-4-stat-value">{trainingPeriod || '—'}</div>
                    </div>
                  </div>
                </div>

                <div className="certificate-template-4-perf">
                  <div className="certificate-template-4-stat">
                    <FaChartLine aria-hidden />
                    <div>
                      <div className="certificate-template-4-stat-label">Percentage</div>
                      <div className="certificate-template-4-stat-value">
                        {pct !== null ? `${pct}%` : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="certificate-template-4-stat">
                    <FaMedal aria-hidden />
                    <div>
                      <div className="certificate-template-4-stat-label">Grade</div>
                      <div className="certificate-template-4-stat-value">{grade || '—'}</div>
                    </div>
                  </div>
                  <div className="certificate-template-4-stat">
                    <FaCogs aria-hidden />
                    <div>
                      <div className="certificate-template-4-stat-label">Skill level</div>
                      <div className="certificate-template-4-stat-value">
                        {skillLevel
                          ? skillLevel.charAt(0).toUpperCase() + skillLevel.slice(1)
                          : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="certificate-template-4-remarks">
                  <span
                    className={`certificate-template-4-remark-tag ${
                      remarkCheck('competent') ? 'certificate-template-4-remark-tag--on' : ''
                    }`}
                  >
                    ✓ Competent
                  </span>
                  <span
                    className={`certificate-template-4-remark-tag ${
                      remarkCheck('highly') ? 'certificate-template-4-remark-tag--on' : ''
                    }`}
                  >
                    ✓ Highly Skilled
                  </span>
                  <span
                    className={`certificate-template-4-remark-tag ${
                      remarkCheck('industry') ? 'certificate-template-4-remark-tag--on' : ''
                    }`}
                  >
                    ✓ Industry Ready
                  </span>
                </div>

                <div className="certificate-template-4-badges">
                  <span className="certificate-template-4-badge">
                    <FaCertificate aria-hidden size={10} /> Vocational
                  </span>
                  <span className="certificate-template-4-badge">
                    <FaAward aria-hidden size={10} /> Industry standards
                  </span>
                </div>

                <div className="certificate-template-4-meta">
                  <span>
                    <strong>Certificate No:</strong> {certNum || '—'}
                  </span>
                  <span>
                    <strong>Issue Date:</strong> {issue || '—'}
                  </span>
                </div>
              </div>

              <aside className="certificate-template-4-side">
                <div className="certificate-template-4-photo">
                  {showPhoto ? (
                    <img src={photoSrc} alt="" onError={() => setPhotoFailed(true)} />
                  ) : !fallbackPhotoFailed ? (
                    <img
                      src={defaultPhoto}
                      alt=""
                      onError={() => setFallbackPhotoFailed(true)}
                    />
                  ) : (
                    <div className="certificate-template-4-photo-ph">Photo</div>
                  )}
                </div>
                <div className="certificate-template-4-qr">
                  <img src={qrSrc} alt="" width={56} height={56} />
                  <div className="certificate-template-4-qr-cap">Verify</div>
                </div>
              </aside>
            </div>

            <footer className="certificate-template-4-footer">
              <div className="certificate-template-4-sign">
                <div className="certificate-template-4-sign-line">Trainer / Instructor</div>
                <div className="certificate-template-4-sign-sub">Signature</div>
              </div>
              <div className="certificate-template-4-sign">
                <div className="certificate-template-4-seal">
                  <FaAward aria-hidden />
                </div>
                <div className="certificate-template-4-sign-line">Institute Seal</div>
                <div className="certificate-template-4-sign-sub">Optional</div>
              </div>
              <div className="certificate-template-4-sign">
                <div className="certificate-template-4-sign-line">{authorityName}</div>
                <div className="certificate-template-4-sign-sub">Authorized Signatory</div>
              </div>
            </footer>
          </div>
        </article>
        </div>
      </div>
    </div>
  )
}

export default CertificateTemplate4
